import type { CSSProperties, KeyboardEvent } from "react";
import type { CatalogCard } from "../../api/client";
import { Card } from "../Card";
import { catalogToCardView } from "../../logic/catalogToCardView";
import { pixelTheme } from "../../theme/pixel";

interface CardFlipProps {
  card: CatalogCard;
  /** When true the card is held face-up regardless of hover. The
   *  marquee uses this to keep both duplicate instances of a clicked
   *  card visually in sync (otherwise as the row scrolls the seam
   *  reveals a still-face-down copy of a "stuck" card). */
  stuck?: boolean;
  /** Fires on click. Caller decides what happens (the landing page
   *  both pins the card face-up and opens the zoom modal on the same
   *  click). */
  onClick?: (card: CatalogCard) => void;
}

// Visual width for marquee tiles. Smaller than `normal`'s natural
// 180 px so the row reads as a strip rather than a row of full cards.
// `Card.tsx` uses container-query units internally, so the rules-text
// font and all internal layout shrink with the container width — we
// get the small-card silhouette but with the rules text the `normal`
// variant ships.
const MARQUEE_CARD_WIDTH = 128;

// One marquee tile. Hover-flips a face-down Card to its real face via
// a 3D rotateY on a `preserve-3d` inner. Both faces are real <Card />
// instances — faceDown renders the deck-of-infinity back, faceUp
// shows the actual art.
export function CardFlip({ card, stuck = false, onClick }: CardFlipProps) {
  const snap = catalogToCardView(card);
  const widthPx = MARQUEE_CARD_WIDTH;
  const heightPx = Math.round((widthPx * 7) / 5);

  function activate() {
    onClick?.(card);
  }

  function onKey(ev: KeyboardEvent<HTMLDivElement>) {
    if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      activate();
    }
  }

  const wrapStyle: CSSProperties = {
    width: widthPx,
    height: heightPx,
    flexShrink: 0,
    cursor: "pointer",
  };

  return (
    <div
      className={`doi-landing-flip${stuck ? " is-flipped" : ""}`}
      role="button"
      tabIndex={0}
      aria-label={`Reveal ${card.name}`}
      style={wrapStyle}
      onClick={activate}
      onKeyDown={onKey}
    >
      <div className="doi-landing-flip-inner">
        <div className="doi-landing-flip-face">
          <Card card={snap} size="normal" theme={pixelTheme} faceDown />
        </div>
        <div className="doi-landing-flip-face doi-landing-flip-back">
          <Card card={snap} size="normal" theme={pixelTheme} />
        </div>
      </div>
    </div>
  );
}
